/**
 * Auth portal (auth-qa) APIs for session: refresh token and logout.
 *
 * - refreshAccessToken(): calls auth portal POST /v1/auth/refresh with credentials;
 *   new access/id tokens are set via HTTP-only cookies (Set-Cookie).
 * - Logout: callInsightLogoutApi() then clear app cookies then redirectToAuthPortalLogout().
 *
 * Silent refresh: startAuthPortalSilentRefresh() runs refresh every 5 minutes so the user
 * never sees expiration. No expiry endpoint exists; this will be obsolete when full silent
 * refresh is introduced. Stop with stopAuthPortalSilentRefresh().
 *
 * axiosInstance uses refreshAccessToken() when useAuthPortalRefresh is enabled (same
 * queue/retry logic as Cognito). TokenRefreshMonitor uses Cognito monitor or auth portal
 * silent refresh depending on authUrl.
 */

import Cookies from 'universal-cookie';
import axiosInstance from '../api/axiosInstance';
import { refreshAccessToken as doAuthPortalRefresh } from './authPortalRefreshService';
import { getAuthPortalLoginUrl } from './authPortal';

/** Silent refresh interval: 5 minutes. User should not notice; we refresh before expiry. */
const SILENT_REFRESH_INTERVAL_MS = 5 * 60 * 1000;

let silentRefreshIntervalId = null;
let silentRefreshInProgress = false;

/**
 * Refresh access token using auth portal API.
 * Refresh token is sent via HTTP-only cookie; new tokens are set via Set-Cookie.
 *
 * @returns {Promise<{ success: boolean }>}
 */
export async function refreshAccessToken() {
  return doAuthPortalRefresh();
}

/**
 * Start silent refresh: call refresh API every 5 minutes so the session stays valid
 * without the user seeing expiration. Safe to call multiple times; only one interval runs.
 *
 * Only redirects to login on 401 (refresh token expired / session revoked).
 * Other failures (network, CORS, 5xx) are logged only; the next interval retries so transient
 * outages do not force logout.
 * Re-runs immediately when the tab becomes visible after being hidden (covers idle/background tabs
 * where setInterval ticks may have been throttled by the browser).
 */
export function startAuthPortalSilentRefresh() {
  if (silentRefreshIntervalId != null) {
    return;
  }

  const runRefresh = async () => {
    if (silentRefreshInProgress) return;
    silentRefreshInProgress = true;
    try {
      await doAuthPortalRefresh();
    } catch (err) {
      const status = err?.status ?? err?.response?.status;
      if (status === 401) {
        // Refresh token is gone / session revoked — stop and redirect to login immediately.
        // Do NOT just stop monitoring: the app must not keep running with a dead session.
        console.warn('Auth portal silent refresh: session expired (401), redirecting to login.');
        stopAuthPortalSilentRefresh();
        window.location.href = getAuthPortalLoginUrl();
      } else {
        // Transient error (CORS, network, 5xx, etc.) — log only; next tick / visibility retry continues.
        console.warn('Auth portal silent refresh failed (non-401, will retry):', err?.message || err);
      }
    } finally {
      silentRefreshInProgress = false;
    }
  };

  silentRefreshIntervalId = setInterval(runRefresh, SILENT_REFRESH_INTERVAL_MS);

  // When the tab comes back into focus after being hidden/idle, the browser may have throttled
  // setInterval ticks. Fire an immediate refresh so the session is always valid on return.
  const handleVisibilityChange = () => {
    if (document.visibilityState === 'visible' && silentRefreshIntervalId != null) {
      runRefresh();
    }
  };
  document.addEventListener('visibilitychange', handleVisibilityChange);

  // Attach cleanup reference so stopAuthPortalSilentRefresh can also remove the listener.
  startAuthPortalSilentRefresh._visibilityHandler = handleVisibilityChange;
}

/**
 * Stop the 5-minute silent refresh interval and remove the visibility listener.
 */
export function stopAuthPortalSilentRefresh() {
  if (silentRefreshIntervalId != null) {
    clearInterval(silentRefreshIntervalId);
    silentRefreshIntervalId = null;
  }
  if (startAuthPortalSilentRefresh._visibilityHandler) {
    document.removeEventListener('visibilitychange', startAuthPortalSilentRefresh._visibilityHandler);
    startAuthPortalSilentRefresh._visibilityHandler = null;
  }
}

/**
 * Call Insight Cloud logout API (POST user/v1/logout).
 * Use this first in the logout flow; then clear app cookies and redirect to Auth Portal logout.
 * With auth portal (OC) login, auth is sent via cookies (axiosInstance uses withCredentials).
 * With Cognito, token is sent from localStorage via axiosInstance request interceptor.
 *
 * @returns {Promise<void>} Resolves when the request completes (success or failure).
 *   Caller should always proceed to clear cookies and redirect; do not block on this.
 */
export async function callInsightLogoutApi() {
  const cookies = new Cookies();
  const email = cookies.get('email') || (typeof localStorage !== 'undefined' && localStorage.getItem('email')) || '';
  const oneCloudUserId = cookies.get('userid') || cookies.get('userId') || '';

  const payload = {
    devicePlateform: 'WEB',
    deviceId: '',
    deviceToken: '',
    email,
    oneCloudEventFlag: 0,
    oneCloudUserId
  };

  try {
    await axiosInstance.post(`${process.env.apiHost}user/v1/logout`, payload, {
      headers: { 'Content-Type': 'application/json; charset=UTF-8' },
      timeout: 60000
    });
  } catch (err) {
    console.warn('Insight logout API call failed (proceeding to clear and redirect):', err?.message || err);
  }
}

/**
 * Call auth portal logout API if required, then caller should clear cookies and redirect.
 *
 * TODO: Implement when API details are shared (if auth portal requires an API call for logout).
 * @returns {Promise<void>}
 */
export async function authPortalLogoutApi() {
  // Placeholder: auth portal logout endpoint TBD (if any)
  // const host = getAuthPortalHost();
  // await fetch(`https://${host}/v1/auth/logout`, { credentials: 'include', ... });
}
